import { useState } from "react";
import { Sparkles, Activity, AlertCircle, CheckCircle2, Fingerprint, Loader2, Send } from "lucide-react";

import {
    type HeuristicFactor,
    type HeuristicQueueRow,
    type HeuristicTestResponse,
    type SimilarityTestMatch,
    useHeuristicQueueRow,
    useHeuristicStats,
    useHeuristicTest,
    useSimilarityTest,
} from "~/lib/heuristic";

/**
 * /heuristic — interactive test page for the Heuristic Scoring Engine.
 *
 * Submit an indicator (URL, domain, IP, hash). The page hits the same code
 * path a customer query takes: synchronous scorer → analysis_queue enqueue.
 * The deep-analysis Temporal workflow runs in the background; this page
 * polls the queue row every 1.5s until it lands `done`.
 */
export function HeuristicPage(): React.ReactElement {
    const stats = useHeuristicStats();
    const test = useHeuristicTest();
    const [input, setInput] = useState("");
    const [lastTest, setLastTest] = useState<HeuristicTestResponse | null>(null);
    const queueRow = useHeuristicQueueRow(lastTest?.queueId ?? null);

    function submit(e: React.FormEvent) {
        e.preventDefault();
        if (!input.trim() || test.isPending) return;
        test.mutate(
            { indicator: input.trim() },
            {
                onSuccess: (data) => {
                    setLastTest(data);
                },
            }
        );
    }

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon">
                                <Sparkles size={14} aria-hidden />
                            </span>
                            Heuristic Engine
                        </span>
                        <h1 className="page-header__title">Test &amp; observability</h1>
                        <p className="page-header__description">
                            Submit a URL, domain, IP, or file hash. The synchronous scorer returns
                            a verdict in &lt;50ms; the async Temporal workflow runs deep analysis
                            (HTTP probe, DNS, WHOIS, TLS cert, YARA on raw HTML) in the background
                            and lands a canonical iocs row when it confirms a malicious
                            indicator.
                        </p>
                    </div>
                </div>
            </header>

            {/* Stats tiles */}
            <section className="section-card" aria-label="Heuristic engine stats">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Last 24 hours</h2>
                        <p className="section-card__subtitle">
                            Live counters from the analysis queue + calibration audit.
                        </p>
                    </div>
                </div>
                <StatsGrid stats={stats.data} loading={stats.isLoading} />
            </section>

            {/* Test form + result */}
            <section className="section-card" aria-label="Test an indicator">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Score an indicator</h2>
                        <p className="section-card__subtitle">
                            URL (with scheme), domain, IPv4, or hash. Type is inferred when
                            unspecified.
                        </p>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    style={{
                        display: "flex",
                        gap: "var(--space-2)",
                        padding: "var(--space-4)",
                        flexWrap: "wrap",
                    }}
                >
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="e.g. https://garanti-fake-bank-login.com or example.com"
                        spellCheck={false}
                        autoComplete="off"
                        style={{
                            flex: "1 1 360px",
                            minWidth: "240px",
                            padding: "var(--space-2) var(--space-3)",
                            borderRadius: "var(--radius-md)",
                            border: "1px solid var(--color-border)",
                            background: "var(--color-surface)",
                            color: "var(--color-text)",
                            fontFamily: "var(--font-family-mono)",
                        }}
                    />
                    <button
                        type="submit"
                        className="btn btn--primary"
                        disabled={!input.trim() || test.isPending}
                    >
                        {test.isPending ? (
                            <>
                                <Loader2
                                    size={14}
                                    aria-hidden
                                    style={{ marginRight: 6, animation: "spin 1s linear infinite" }}
                                />
                                Scoring…
                            </>
                        ) : (
                            <>
                                <Send size={14} aria-hidden style={{ marginRight: 6 }} />
                                Score
                            </>
                        )}
                    </button>
                </form>

                {test.isError && (
                    <div
                        className="empty-state"
                        style={{ borderTop: "1px solid var(--color-border)" }}
                    >
                        <AlertCircle size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">
                            {(test.error as Error).message ?? "Request failed"}
                        </div>
                    </div>
                )}

                {lastTest && (
                    <ResultPanel response={lastTest} queueRow={queueRow.data ?? null} />
                )}
            </section>

            <SimilaritySection />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Phase F.5 — Fuzzy-hash similarity test panel.
// ---------------------------------------------------------------------------

function SimilaritySection(): React.ReactElement {
    const sim = useSimilarityTest();
    const [tlsh, setTlsh] = useState("");
    const [ssdeep, setSsdeep] = useState("");
    const [imphash, setImphash] = useState("");

    function submit(e: React.FormEvent) {
        e.preventDefault();
        if (sim.isPending) return;
        const body: Record<string, string> = {};
        if (tlsh.trim()) body.tlsh = tlsh.trim();
        if (ssdeep.trim()) body.ssdeep = ssdeep.trim();
        if (imphash.trim()) body.imphash = imphash.trim();
        if (Object.keys(body).length === 0) return;
        sim.mutate(body);
    }

    const data = sim.data;

    return (
        <section className="section-card" aria-label="Fuzzy-hash similarity">
            <div className="section-card__header">
                <div className="section-card__title-group">
                    <h2 className="section-card__title">
                        <Fingerprint size={14} aria-hidden style={{ marginRight: 6, verticalAlign: -2 }} />
                        Fuzzy-hash similarity
                    </h2>
                    <p className="section-card__subtitle">
                        Paste a TLSH, SSDEEP, or imphash to find similar known-malicious
                        samples in the catalog. Mirrors{" "}
                        <code>POST /v2/similarity/file</code> — same matcher,
                        same envelope. <a href="/docs/integration#int-65-fuzzy-hash-similarity-post-v2similarityfile">Read the integration guide</a>.
                    </p>
                </div>
            </div>

            <form
                onSubmit={submit}
                style={{
                    display: "grid",
                    gap: "var(--space-2)",
                    padding: "var(--space-4)",
                }}
            >
                <SimilarityInput
                    label="TLSH"
                    placeholder="T1A1B2C3D4E5F6... (T1-prefixed or raw 68/70 hex)"
                    value={tlsh}
                    onChange={setTlsh}
                />
                <SimilarityInput
                    label="SSDEEP"
                    placeholder="192:base64chunk1abc:base64chunk2def"
                    value={ssdeep}
                    onChange={setSsdeep}
                />
                <SimilarityInput
                    label="imphash"
                    placeholder="32-char hex (PE files only)"
                    value={imphash}
                    onChange={setImphash}
                />
                <div>
                    <button
                        type="submit"
                        className="btn btn--primary"
                        disabled={sim.isPending || (!tlsh.trim() && !ssdeep.trim() && !imphash.trim())}
                    >
                        {sim.isPending ? (
                            <>
                                <Loader2 size={14} aria-hidden style={{ marginRight: 6, animation: "spin 1s linear infinite" }} />
                                Searching…
                            </>
                        ) : (
                            <>
                                <Send size={14} aria-hidden style={{ marginRight: 6 }} />
                                Find similar
                            </>
                        )}
                    </button>
                </div>
            </form>

            {sim.isError && (
                <div className="empty-state" style={{ borderTop: "1px solid var(--color-border)" }}>
                    <AlertCircle size={20} className="empty-state__icon" aria-hidden />
                    <div className="empty-state__title">{(sim.error as Error).message ?? "Request failed"}</div>
                </div>
            )}

            {data && <SimilarityResultPanel data={data} />}
        </section>
    );
}

function SimilarityInput({
    label,
    placeholder,
    value,
    onChange,
}: {
    label: string;
    placeholder: string;
    value: string;
    onChange: (s: string) => void;
}): React.ReactElement {
    return (
        <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", alignItems: "center", gap: "var(--space-2)" }}>
            <label className="text-text-muted" style={{ fontSize: "var(--font-size-12)" }}>
                {label}
            </label>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                spellCheck={false}
                autoComplete="off"
                style={{
                    padding: "var(--space-2) var(--space-3)",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--color-border)",
                    background: "var(--color-surface)",
                    color: "var(--color-text)",
                    fontFamily: "var(--font-family-mono)",
                    fontSize: "var(--font-size-12)",
                }}
            />
        </div>
    );
}

function SimilarityResultPanel({
    data,
}: {
    data: { decision: string; strategy: string; matches: SimilarityTestMatch[]; generation: number };
}): React.ReactElement {
    const decisionPill =
        data.decision === "similar-malicious"
            ? "var(--color-danger-text, #ef4444)"
            : data.decision === "similar-suspicious"
              ? "var(--color-warning-text, #f59e0b)"
              : "var(--color-text-muted)";

    return (
        <div
            style={{
                borderTop: "1px solid var(--color-border)",
                padding: "var(--space-4)",
                display: "grid",
                gap: "var(--space-3)",
            }}
        >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "var(--space-3)" }}>
                <div className="surface" style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)" }}>
                    <div className="text-text-muted" style={{ fontSize: "var(--font-size-11)", textTransform: "uppercase", marginBottom: 4 }}>
                        Decision
                    </div>
                    <div style={{ fontSize: "var(--font-size-14)", fontWeight: 600, color: decisionPill }}>
                        {data.decision}
                    </div>
                </div>
                <div className="surface" style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)" }}>
                    <div className="text-text-muted" style={{ fontSize: "var(--font-size-11)", textTransform: "uppercase", marginBottom: 4 }}>
                        Strategy
                    </div>
                    <div className="tabular" style={{ fontSize: "var(--font-size-14)", fontWeight: 600 }}>
                        {data.strategy}
                    </div>
                </div>
                <div className="surface" style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)" }}>
                    <div className="text-text-muted" style={{ fontSize: "var(--font-size-11)", textTransform: "uppercase", marginBottom: 4 }}>
                        Matches
                    </div>
                    <div className="tabular" style={{ fontSize: "var(--font-size-14)", fontWeight: 600 }}>
                        {data.matches.length}
                    </div>
                </div>
                <div className="surface" style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)" }}>
                    <div className="text-text-muted" style={{ fontSize: "var(--font-size-11)", textTransform: "uppercase", marginBottom: 4 }}>
                        Catalog gen
                    </div>
                    <div className="tabular" style={{ fontSize: "var(--font-size-14)", fontWeight: 600 }}>
                        {data.generation}
                    </div>
                </div>
            </div>

            {data.matches.length === 0 ? (
                <div className="empty-state" style={{ borderTop: "1px solid var(--color-border)" }}>
                    <Fingerprint size={20} className="empty-state__icon" aria-hidden />
                    <div className="empty-state__title">No similar samples found</div>
                </div>
            ) : (
                <div style={{ display: "grid", gap: "var(--space-2)" }}>
                    {data.matches.map((m, i) => (
                        <div
                            key={`${m.sha256}-${i}`}
                            className="surface"
                            style={{
                                padding: "var(--space-3)",
                                borderRadius: "var(--radius-md)",
                                display: "grid",
                                gap: "var(--space-1)",
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-2)" }}>
                                <a
                                    href={m.evidenceUrl ?? `https://bazaar.abuse.ch/sample/${m.sha256}/`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="tabular"
                                    style={{ fontSize: "var(--font-size-12)", fontFamily: "var(--font-family-mono)", textDecoration: "none" }}
                                >
                                    {m.sha256}
                                </a>
                                <span
                                    className="status-pill"
                                    style={{
                                        fontSize: "var(--font-size-11)",
                                        background: m.strength === "strong" ? "var(--color-danger-subtle, #fee2e2)" : "var(--color-warning-subtle, #fef3c7)",
                                        color: m.strength === "strong" ? "var(--color-danger-text, #ef4444)" : "var(--color-warning-text, #d97706)",
                                        padding: "2px 8px",
                                        borderRadius: "var(--radius-pill)",
                                    }}
                                >
                                    {m.strength}
                                </span>
                            </div>
                            <div className="text-text-muted" style={{ fontSize: "var(--font-size-12)" }}>
                                <span className="tabular">{m.reason}</span>
                                {" · severity "}
                                {m.severity}
                                {m.tags && m.tags.length > 0 && (
                                    <>
                                        {" · "}
                                        {m.tags.slice(0, 4).join(", ")}
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatsGrid({
    stats,
    loading,
}: {
    stats: ReturnType<typeof useHeuristicStats>["data"];
    loading: boolean;
}): React.ReactElement {
    const cards: Array<{ label: string; value: string; sub?: string }> = stats
        ? [
              {
                  label: "Queue depth",
                  value: stats.queueDepth.toLocaleString(),
                  sub: `${stats.queueRunning.toLocaleString()} running`,
              },
              {
                  label: "Verdicts (24h)",
                  value: stats.verdicts24h.toLocaleString(),
                  sub: `${stats.hotVerdicts.toLocaleString()} hot in cache`,
              },
              {
                  label: "Calibrations (24h)",
                  value: stats.calibrations24h.toLocaleString(),
                  sub: `${stats.agreementRate24h.toFixed(1)}% agreement`,
              },
              {
                  label: "Deep latency p50",
                  value: stats.latencyP50Ms === 0 ? "—" : `${stats.latencyP50Ms.toLocaleString()} ms`,
                  sub: stats.latencyP99Ms === 0 ? undefined : `p99 ${stats.latencyP99Ms.toLocaleString()} ms`,
              },
              {
                  label: "Tier-2 trigger",
                  value: `${stats.tier2TriggerRate.toFixed(1)}%`,
                  sub: "borderline 40-60",
              },
              {
                  label: "Promoted IoCs (24h)",
                  value: stats.promotedIocs24h.toLocaleString(),
                  sub: "deep-analysis confirmed",
              },
          ]
        : [];

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "var(--space-3)",
                padding: "var(--space-4)",
            }}
        >
            {loading
                ? [1, 2, 3, 4, 5, 6].map((i) => (
                      <div
                          key={i}
                          style={{
                              background: "var(--color-surface)",
                              padding: "var(--space-3)",
                              borderRadius: "var(--radius-md)",
                              minHeight: "84px",
                          }}
                      />
                  ))
                : cards.map((c) => (
                      <div
                          key={c.label}
                          className="surface"
                          style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)" }}
                      >
                          <div
                              className="text-text-muted"
                              style={{
                                  fontSize: "var(--font-size-11)",
                                  fontWeight: 500,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  marginBottom: "var(--space-2)",
                              }}
                          >
                              {c.label}
                          </div>
                          <div
                              className="tabular"
                              style={{ fontSize: "var(--font-size-22)", fontWeight: 600 }}
                          >
                              {c.value}
                          </div>
                          {c.sub && (
                              <div
                                  className="text-text-muted"
                                  style={{ fontSize: "var(--font-size-12)", marginTop: 4 }}
                              >
                                  {c.sub}
                              </div>
                          )}
                      </div>
                  ))}
        </div>
    );
}

function ResultPanel({
    response,
    queueRow,
}: {
    response: HeuristicTestResponse;
    queueRow: HeuristicQueueRow | null;
}): React.ReactElement {
    const v = response.verdict;
    const factors: HeuristicFactor[] = v.factors ?? [];
    const deepStatus = queueRow?.status ?? "queued";
    const deepDone = deepStatus === "done";
    const promoteReason =
        queueRow?.result && typeof queueRow.result["promote"] === "object"
            ? ((queueRow.result["promote"] as { reason?: string }).reason ?? "")
            : "";
    const promoteWritten = ["yara_match", "http_200_and_san_sprawl", "http_200_and_fresh_cert"].includes(promoteReason);
    const promoteExplanation = explainPromotion(promoteReason, deepDone);

    return (
        <div
            style={{
                borderTop: "1px solid var(--color-border)",
                padding: "var(--space-4)",
                display: "grid",
                gap: "var(--space-4)",
            }}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "var(--space-3)",
                }}
            >
                <Tile
                    label="Indicator"
                    value={response.indicator}
                    sub={response.indicatorType.toUpperCase()}
                    mono
                />
                <Tile
                    label="Sync verdict"
                    value={v.decision}
                    sub={`score ${v.score}${response.cacheHit ? " · cache hit" : ""}`}
                />
                <Tile
                    label="Deep analysis"
                    value={deepStatus}
                    sub={
                        deepDone
                            ? `score ${queueRow?.deepScore ?? 0} · ${queueRow?.latencyMs ?? 0} ms`
                            : "running…"
                    }
                />
                <Tile
                    label="Promotion"
                    value={deepDone ? (promoteWritten ? "promoted" : "not promoted") : "pending"}
                    sub={deepDone ? promoteExplanation : "deep analysis still running"}
                />
            </div>

            {/* Inline help: demystify the Promotion tile so an operator
                doesn't have to dig into the docs to understand it. */}
            {deepDone && (
                <div
                    className="surface"
                    style={{
                        padding: "var(--space-3) var(--space-4)",
                        borderRadius: "var(--radius-md)",
                        background: "var(--color-surface-subtle, var(--color-surface))",
                        fontSize: "var(--font-size-12)",
                        color: "var(--color-text-muted)",
                    }}
                >
                    <strong style={{ color: "var(--color-text)" }}>What is "promotion"?</strong>{" "}
                    Heuristic verdicts live in a 5-min cache; we only commit a
                    permanent <code>iocs</code> row when deep analysis confirms
                    with corroborating evidence (YARA match OR HTTP 200 +
                    suspicious cert). When promoted, future customer queries on
                    this indicator return a canonical <code>malicious</code>{" "}
                    verdict instead of <code>heuristic-*</code>. The reason{" "}
                    <code>{promoteReason || "—"}</code> means:{" "}
                    <em>{promoteExplanation}</em>
                </div>
            )}

            {factors.length > 0 && (
                <div>
                    <h3
                        style={{
                            fontSize: "var(--font-size-13)",
                            fontWeight: 600,
                            margin: "0 0 var(--space-2) 0",
                        }}
                    >
                        Sync factors
                    </h3>
                    <div style={{ display: "grid", gap: "var(--space-2)" }}>
                        {factors.map((f, i) => (
                            <div
                                key={`${f.name}-${i}`}
                                className="surface"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr auto",
                                    gap: "var(--space-2)",
                                    padding: "var(--space-2) var(--space-3)",
                                    borderRadius: "var(--radius-md)",
                                    alignItems: "center",
                                }}
                            >
                                <div>
                                    <div
                                        className="tabular"
                                        style={{
                                            fontSize: "var(--font-size-12)",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {f.name}
                                    </div>
                                    <div
                                        className="text-text-muted"
                                        style={{ fontSize: "var(--font-size-12)" }}
                                    >
                                        {f.value}
                                    </div>
                                </div>
                                <div
                                    className="tabular"
                                    style={{
                                        fontSize: "var(--font-size-13)",
                                        fontWeight: 600,
                                    }}
                                >
                                    +{f.contribution}
                                    <span
                                        className="text-text-muted"
                                        style={{ fontSize: "var(--font-size-11)", marginLeft: 4 }}
                                    >
                                        / {f.weight}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {deepDone && queueRow?.result && (
                <details>
                    <summary
                        style={{
                            cursor: "pointer",
                            fontSize: "var(--font-size-13)",
                            fontWeight: 600,
                            marginBottom: "var(--space-2)",
                        }}
                    >
                        Deep analysis raw result
                    </summary>
                    <pre
                        className="surface"
                        style={{
                            padding: "var(--space-3)",
                            borderRadius: "var(--radius-md)",
                            fontSize: "var(--font-size-11)",
                            overflow: "auto",
                            maxHeight: 360,
                            margin: 0,
                            fontFamily: "var(--font-family-mono)",
                        }}
                    >
                        {JSON.stringify(queueRow.result, null, 2)}
                    </pre>
                </details>
            )}

            {!deepDone && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--space-2)",
                        color: "var(--color-text-muted)",
                        fontSize: "var(--font-size-12)",
                    }}
                >
                    <Activity size={14} aria-hidden />
                    Polling Temporal queue every 1.5s · workflow id{" "}
                    <span className="tabular">{queueRow?.workflowId ?? response.queueId}</span>
                </div>
            )}

            {deepDone && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--space-2)",
                        color: "var(--color-success-text, #4ade80)",
                        fontSize: "var(--font-size-12)",
                    }}
                >
                    <CheckCircle2 size={14} aria-hidden />
                    Deep analysis complete in {queueRow?.latencyMs ?? 0} ms
                </div>
            )}
        </div>
    );
}

// explainPromotion turns the workflow's terse promote.reason string into a
// human-readable explanation suitable for the Promotion tile sub-text and
// the inline help paragraph beneath it. Keeps the test page self-contained
// — an operator shouldn't need to read internal/heuristic/promote.go to
// understand what they're looking at.
function explainPromotion(reason: string, done: boolean): string {
    if (!done) return "";
    switch (reason) {
        case "yara_match":
            return "phishing-tagged YARA rule matched the rendered HTML. iocs row written.";
        case "http_200_and_fresh_cert":
            return "live site responding 200 with TLS cert <48h old. iocs row written.";
        case "http_200_and_san_sprawl":
            return "live site responding 200 with cert covering >20 SANs. iocs row written.";
        case "deep_score_below_threshold":
            return "deep analysis disagreed with the heuristic — score too low to commit. Stayed in cache only.";
        case "no_corroboration":
            return "high deep score but no YARA hit and no suspicious cert posture. Stayed in cache only.";
        case "":
            return "no reason recorded.";
        default:
            return reason;
    }
}

function Tile({
    label,
    value,
    sub,
    mono,
}: {
    label: string;
    value: string;
    sub?: string;
    mono?: boolean;
}): React.ReactElement {
    return (
        <div
            className="surface"
            style={{ padding: "var(--space-3)", borderRadius: "var(--radius-md)" }}
        >
            <div
                className="text-text-muted"
                style={{
                    fontSize: "var(--font-size-11)",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "var(--space-2)",
                }}
            >
                {label}
            </div>
            <div
                className={mono ? "tabular" : ""}
                style={{
                    fontSize: "var(--font-size-14)",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontFamily: mono ? "var(--font-family-mono)" : undefined,
                }}
                title={value}
            >
                {value}
            </div>
            {sub && (
                <div
                    className="text-text-muted"
                    style={{ fontSize: "var(--font-size-12)", marginTop: 4 }}
                >
                    {sub}
                </div>
            )}
        </div>
    );
}
